export type LatestSaveOutcome = 'saved' | 'retry' | 'discard';

/**
 * Serialises "latest value wins" autosaves.
 *
 * A value scheduled while a request is in flight is drained immediately after
 * that request succeeds. If a request fails, the attempted value is restored
 * only when no newer value is already pending.
 */
export function createLatestSaveQueue<T>(
  save: (value: T) => Promise<LatestSaveOutcome>,
) {
  let pending: T | null = null;
  let inFlight: Promise<boolean> | null = null;

  async function drain(): Promise<boolean> {
    while (pending !== null) {
      const value = pending;
      pending = null;
      const outcome = await save(value);

      if (outcome === 'retry') {
        // A newer edit scheduled during the request is more current than the
        // failed value and must win.
        if (pending === null) pending = value;
        return false;
      }
      if (outcome === 'discard') {
        pending = null;
        return false;
      }
    }
    return true;
  }

  return {
    schedule(value: T) {
      pending = value;
    },
    clear() {
      pending = null;
    },
    hasWork() {
      return pending !== null || inFlight !== null;
    },
    flush(): Promise<boolean> {
      if (inFlight) return inFlight;
      if (pending === null) return Promise.resolve(true);
      inFlight = drain().finally(() => {
        inFlight = null;
      });
      return inFlight;
    },
  };
}
